/**
 * voice-v8 — Werkzeugkarten: Blender und 3D-Modelle (Suchen, Importieren).
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { aufsetzenZeile, pruefungZeile, pythonAusgabe } from "../blender-karten-text.js";
import { baueKarte, chips, dateiZeile, fehlerBlock, kartenTitel, kuerzeListe, leerHinweis, liste, listenEintrag, weitereHinweis, wertZeile } from "../karte.js";
import { dateiName, fuerMenschen } from "../karten-text.js";
import { menschlicherFehler } from "../fehler-worte.js";
import { werkzeugName } from "../werkzeug-namen.js";
import { trimText, unwrapResult } from "./hilfe.js";

/** „1,2 MB“ aus Bytes — eine Schreibweise für alle Karten. */
function megabyte(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/**
 * Blender · Speichern — aus den Prüfwerten des Servers (tools/blender-speichern.ts):
 * die Datei als Zeile (Name sichtbar, Ort in Worten, Pfad im Tooltip), die
 * Pillen „gespeichert“ und die Größe, die Sicherungskopie als leise Zeile.
 * Der `hinweis` des Werkzeugs ist für das Modell geschrieben und bleibt draußen.
 */
function blenderSpeichernZeilen(el: HTMLElement, r: Record<string, unknown>): void {
  if (typeof r.pfad === "string" && r.pfad) dateiZeile(el, r.pfad);
  const pillen = ["gespeichert"];
  if (typeof r.bytes === "number" && r.bytes > 0) pillen.push(megabyte(r.bytes));
  chips(el, pillen);
  const saetze = [r.geprueft === true ? "Auf dem Mac geprüft." : ""];
  if (typeof r.sicherung === "string" && r.sicherung) {
    saetze.push(r.sicherung_anker === true ? "Sicherungskopie der Fassung davor angelegt (bleibt länger)." : "Sicherungskopie der Fassung davor angelegt.");
  } else if (r.vorher_bytes === null || r.vorher_bytes === undefined) {
    saetze.push("Neue Datei — vorher lag hier nichts.");
  }
  const hinweis = saetze.filter(Boolean).join(" ");
  if (hinweis) leerHinweis(el, hinweis);
}

export function buildBlenderCard(tool: string, result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte(tool.replace(/_/g, "-"));
  el.appendChild(kartenTitel(werkzeugName(tool)));
  if (r.ok === false || (typeof r.error === "string" && r.error)) {
    el.appendChild(fehlerBlock(menschlicherFehler(r, "Blender hat gerade nicht geantwortet.")));
    return el;
  }
  if (tool === "blender_speichern") {
    blenderSpeichernZeilen(el, r);
    return el;
  }
  // 07.10.2026: Python, Prüfung und Aufsetzen — eine Zeile, die Rohdaten aufklappbar (frontend/lib/blender-karten-text.ts).
  const kurz =
    tool === "blender_python" ? pythonAusgabe(r) : tool === "blender_pruefen" ? pruefungZeile(r) : tool === "blender_aufsetzen" ? aufsetzenZeile(r) : null;
  if (kurz) {
    el.appendChild(blenderZeile(kurz.zeile, kurz.roh));
    return el;
  }
  const body = document.createElement("div");
  body.className = "tool-browser-detail";
  let detail: string;
  if (tool === "blender_szene") {
    const s = (r.szene ?? {}) as { objekte_gesamt?: unknown; datei?: unknown; treffer_gesamt?: unknown };
    const n = typeof s.objekte_gesamt === "number" ? s.objekte_gesamt : 0;
    const datei = typeof s.datei === "string" && s.datei ? s.datei.split("/").pop() : "ungespeichert";
    // 06.10.2026 (Ticket 3020): mit Filter die Zahl der Treffer.
    detail = typeof s.treffer_gesamt === "number" ? `${s.treffer_gesamt} von ${n} Objekten · ${datei}` : `${n} Objekte · ${datei}`;
  } else if (tool === "blender_render") {
    const g = r.groesse as [number, number] | undefined;
    const info = (r.render ?? {}) as { engine?: unknown; sekunden?: unknown };
    detail = [
      Array.isArray(g) ? `${g[0]}×${g[1]}` : "Render-Bild",
      typeof info.engine === "string" ? info.engine.replace(/^BLENDER_/, "").toLowerCase() : "",
      typeof info.sekunden === "number" ? `${String(info.sekunden).replace(".", ",")} s` : "",
    ].filter(Boolean).join(" · ");
  } else if (tool === "blender_oeffnen") {
    // 11.10.2026: die Karte nennt nur, was das Ergebnis belegt — „geöffnet“ nur bei geoeffnet+geprueft, sonst Liste oder „bereits offen“.
    const dateien = Array.isArray(r.dateien) ? r.dateien.length : null;
    detail =
      r.geoeffnet === true && r.geprueft === true
        ? `${typeof r.datei === "string" ? dateiName(r.datei) : "Datei"} geöffnet · ${typeof r.objekte === "number" ? r.objekte : 0} Objekte`
        : r.bereits_offen === true
          ? `${typeof r.name === "string" ? dateiName(r.name) : "Datei"} ist bereits offen`
          : dateien !== null
            ? `${dateien} Projektdatei${dateien === 1 ? "" : "en"}`
            : "nicht geöffnet";
  } else if (tool === "blender_objekt_stellen" || tool === "blender_herde_stellen") {
    // 15.10.2026 (Welle B): nur Felder — wie viele, wo, und was nicht passt.
    const maengel = [
      r.frei === false || (typeof r.nicht_frei === "number" && r.nicht_frei > 0) ? "nicht frei" : "",
      r.untergrund === "schlecht" || (typeof r.untergrund_schlecht === "number" && r.untergrund_schlecht > 0) ? "Untergrund falsch" : "",
      r.abstand === "unterschritten" || (typeof r.abstand_unterschritten === "number" && r.abstand_unterschritten > 0) ? "Abstand zu klein" : "",
      r.im_bild === false || (typeof r.nicht_im_bild === "number" && r.nicht_im_bild > 0) ? "nicht im Bild" : "",
    ].filter(Boolean);
    const was = typeof r.anzahl === "number" ? `${r.anzahl} hingestellt` : r.bewegt === false ? "stand schon passend" : "hingestellt";
    detail = [was, ...maengel].join(" · ");
  } else if (tool === "blender_nahaufnahme") {
    // 15.10.2026 (Welle B): Name aus der Anfrage (geprüft), Anteil im Kamerabild — nur Felder, nie der Hinweis an das Modell.
    const anteil = typeof r.pixelanteil === "number" ? `${String(r.pixelanteil).replace(".", ",")} % im Bild` : r.im_bild === false ? "nicht im Bild" : "";
    detail = [typeof r.objekt === "string" ? fuerMenschen(r.objekt, "Objekt") : "Objekt", anteil, r.zu_klein === true ? "zu klein" : ""].filter(Boolean).join(" · ");
  } else if (tool === "mac_anbindungen") {
    const liste = Array.isArray(r.anbindungen) ? (r.anbindungen as { name?: unknown; eingerichtet?: unknown; verbunden?: unknown; installiert?: unknown }[]) : [];
    detail =
      liste
        .map((a) => {
          const zustand = a.verbunden ? "verbunden" : a.eingerichtet ? "eingerichtet" : a.installiert ? "noch nicht eingerichtet" : "nicht vorhanden";
          return `${typeof a.name === "string" ? a.name : "?"}: ${zustand}`;
        })
        .join(" · ") || "keine Programme";
  } else {
    const g = r.groesse as [number, number] | undefined;
    detail = Array.isArray(g) ? `${g[0]}×${g[1]}` : "Bild der 3D-Ansicht";
  }
  body.textContent = detail;
  el.appendChild(body);
  return el;
}

/**
 * Modelle · Suche / Import. (15.10.2026) Die Treffer als Liste — je Eintrag
 * Titel, darunter Autor · Lizenz · Größe (Martin 14.10.: „Warum steht der
 * Sheep fünfmal hintereinander?“); ohne Treffer der Satz des Werkzeugs, soweit
 * er an Martin gerichtet ist. Der Import als Zeilen: Modell, Autor, Lizenz,
 * Maße — und „wartet auf dein Ja“, solange das Ja-Tor hält.
 */
export function buildModellCard(tool: string, result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte(tool.replace(/_/g, "-"));
  el.appendChild(kartenTitel(werkzeugName(tool)));
  if (r.ok === false || (typeof r.error === "string" && r.error)) {
    el.appendChild(fehlerBlock(menschlicherFehler(r, "Das Modell ließ sich nicht holen.")));
    return el;
  }
  if (tool === "modell_importieren") {
    if (typeof r.titel === "string" && r.titel) wertZeile(el, "Modell", r.titel);
    if (typeof r.autor === "string" && r.autor) wertZeile(el, "Autor", r.autor);
    const masse = ["breite_m", "tiefe_m", "hoehe_m"].map((k) => (typeof r[k] === "number" ? String(r[k]).replace(".", ",") : null));
    if (masse.every((m) => m !== null)) wertZeile(el, "Maße", `${masse.join(" × ")} m`);
    const pillen = [r.importiert === true ? "in der Szene" : "nicht übernommen"];
    if (typeof r.lizenz === "string" && r.lizenz) pillen.push(r.lizenz);
    if (r.am_boden === true) pillen.push("auf dem Boden");
    chips(el, pillen);
    if (r.importiert === true) leerHinweis(el, "Die Szene ist geändert und noch nicht gespeichert.");
    return el;
  }
  const treffer = Array.isArray(r.treffer) ? (r.treffer as Record<string, unknown>[]) : [];
  if (treffer.length === 0) {
    // Der Satz an Martin, gekürzt auf eine Zeile — der Rest des Hinweises gilt dem Modell.
    leerHinweis(el, typeof r.hinweis === "string" ? trimText(fuerMenschen(r.hinweis, "Nichts gefunden."), 300) : "Nichts gefunden.");
    return el;
  }
  if (typeof r.quelle === "string" && r.quelle) chips(el, [r.quelle]);
  const ul = liste(el);
  const gekuerzt = kuerzeListe(treffer);
  for (const t of gekuerzt.sichtbar) {
    const teile = [
      typeof t.autor === "string" ? t.autor : null,
      typeof t.lizenz === "string" ? t.lizenz : null,
      typeof t.dreiecke === "number" ? `${t.dreiecke} Dreiecke` : null,
      typeof t.format === "string" ? t.format : null,
      typeof t.groesse_kb === "number" ? `${t.groesse_kb} KB` : null,
    ].filter((x): x is string => !!x);
    ul.appendChild(listenEintrag(typeof t.titel === "string" && t.titel ? t.titel : "Modell", teile.join(" · ") || undefined));
  }
  weitereHinweis(el, gekuerzt.rest);
  return el;
}

/**
 * Eine Zeile — und wenn es Rohdaten gibt, ist genau diese Zeile der Knopf zum Aufklappen (<details>, ohne Skript,
 * mit Tastatur bedienbar). Der Rohtext bleibt vollständig da: nichts wird verschwiegen, nur eingeklappt.
 */
function blenderZeile(zeile: string, roh: string | null): HTMLElement {
  if (!roh) {
    const body = document.createElement("div");
    body.className = "tool-browser-detail";
    body.textContent = zeile;
    return body;
  }
  const box = document.createElement("details");
  box.className = "tool-blender-ausgabe";
  const kopf = document.createElement("summary");
  kopf.className = "tool-browser-detail tool-blender-ausgabe-kopf";
  kopf.textContent = zeile;
  const pre = document.createElement("pre");
  pre.className = "tool-card-raw";
  pre.textContent = roh;
  box.append(kopf, pre);
  return box;
}
